import { useState } from 'react';
import axios from 'axios';
import { isLocalhost } from '../utils/isLocalhost';
import { PriceOptions } from '../Contexts/OrderContext';
import { PreferenceOptions } from '../Contexts/PaymentContext';

const useApi = () => {
  const [loading, setLoading] = useState(false);

  const apiEnviroment = process.env.REACT_APP_API_ENVIRONMENT;

  const getUrl = (path: string, querystring?: string) =>
    isLocalhost
      ? `http://127.0.0.1:5001/el-rayo-dev/us-central1${path}${
          querystring ?? ''
        }`
      : `https:/${path}${apiEnviroment}${path}${querystring ?? ''}`;

  const validatePDF = async (file: any) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);

      const url = getUrl(`/validatePDF`);

      const { data } = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setLoading(false);

      return data.length;
    } catch (ex) {
      console.log(ex);
      return null;
    }
  };

  const calculatePrice = async (priceOptions: PriceOptions) => {
    try {
      setLoading(true);

      const url = getUrl(`/calculatePrice`);

      const { data } = await axios.post(url, priceOptions);
      setLoading(false);

      return data;
    } catch (ex) {
      console.log(ex);
      return null;
    }
  };

  const calculateShipping = async (priceOptions: PriceOptions) => {
    try {
      setLoading(true);

      const url = getUrl(`/calculateShippingCost`);

      const { data } = await axios.post(url, priceOptions);
      setLoading(false);

      return data;
    } catch (ex) {
      console.log(ex);
      return null;
    }
  };

  const createMPPreference = async (preferenceOptions: PreferenceOptions) => {
    try {
      setLoading(true);

      const url = getUrl(`/createMPPreference`);

      const { data } = await axios.post(url, preferenceOptions);
      setLoading(false);

      return data;
    } catch (ex) {
      console.log(ex);
      return null;
    }
  };

  const selectTable = async (table: string) => {
    try {
      setLoading(true);

      const url = getUrl(`/select`, `?entity=${table}`);

      const { data } = await axios.get(url);
      setLoading(false);

      return data;
    } catch (ex) {
      console.log(ex);
      return null;
    }
  };

  const selectTableByOrderId = async (table: string, orderId: string) => {
    try {
      setLoading(true);

      const url = getUrl(`/select`, `?entity=${table}&order=${orderId}`);

      const { data } = await axios.get(url);
      setLoading(false);

      return data;
    } catch (ex) {
      console.log(ex);
      return null;
    }
  };

  const selectTableByTrackingCode = async (
    table: string,
    trackingCode: string
  ) => {
    try {
      setLoading(true);

      const url = getUrl(
        `/select`,
        `?entity=${table}&tracking_code=${trackingCode}`
      );

      const { data } = await axios.get(url);
      setLoading(false);

      return data;
    } catch (ex) {
      console.log(ex);
      return null;
    }
  };

  const updateTable = async (table: string, updateData: any) => {
    try {
      setLoading(true);

      const url = getUrl(`/update`, `?entity=${table}`);

      const { data } = await axios.put(url, updateData);
      setLoading(false);

      return data;
    } catch (ex) {
      console.log(ex);
      return null;
    }
  };

  const insertTable = async (table: string, insertData: any) => {
    try {
      setLoading(true);

      const url = getUrl(`/insert`, `?entity=${table}`);

      const { data } = await axios.post(url, insertData);
      setLoading(false);

      return data;
    } catch (ex) {
      console.log(ex);
      return null;
    }
  };

  const deleteTable = async (table: string, { id }: any) => {
    try {
      setLoading(true);

      const url = getUrl(`/delete`, `?entity=${table}&id=${id}`);

      const { data } = await axios.delete(url);
      setLoading(false);

      return data;
    } catch (ex) {
      console.log(ex);
      return null;
    }
  };

  const bulkInsertTable = async (table: string, insertData: any[]) => {
    try {
      setLoading(true);

      const url = getUrl(`/bulkInsert`, `?entity=${table}`);

      const { data } = await axios.post(url, insertData);
      setLoading(false);

      return data;
    } catch (ex) {
      console.log(ex);
      return null;
    }
  };

  const getReport = async (dateParam: string) => {
    try {
      setLoading(true);
      // Construye la URL correctamente incluso si dateParam es 'all'
      const url = getUrl('/report', dateParam ? `?date=${dateParam}` : '');
      const { data } = await axios.get(url);

      if (!data) {
        throw new Error('No se recibieron datos del servidor');
      }

      setLoading(false);
      return data;
    } catch (ex) {
      console.error('Error al obtener el reporte:', ex);
      setLoading(false);
      return {
        success: false,
        error:
          (ex as any)?.response?.data?.error || 'Error al generar el reporte',
      };
    }
  };

  const getOrderZipUrl = (orderId: string) =>
    getUrl('/descargarOrdenZip', `?orderId=${encodeURIComponent(orderId)}`);

  const downloadOrderZip = async (orderId: string) => {
    const url = getOrderZipUrl(orderId);
    const { data, headers } = await axios.get(url, { responseType: 'blob' });
    const blob = new Blob([data], { type: 'application/zip' });
    const cd = headers['content-disposition'] as string | undefined;
    let filename = `Archivos de la orden - ${orderId}.zip`;
    if (cd) {
      const m = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i.exec(cd);
      if (m) filename = decodeURIComponent(m[1].replace(/['"]/g, ''));
    }

    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  };

  const getPreciosCopias = async () => {
    try {
      setLoading(true);

      const url = getUrl(`/getPreciosCopias`);

      const { data } = await axios.get(url);
      setLoading(false);

      return data;
    } catch (ex) {
      console.error('Error al obtener precios de copias:', ex);
      setLoading(false);
      return {
        success: false,
        error: 'Error al obtener los precios de copias'
      };
    }
  };

  const updatePrecioCopia = async (id: number, precio: number) => {
    try {
      setLoading(true);

      const url = getUrl(`/updatePrecioCopia`);

      const { data } = await axios.put(url, { id, precio });
      setLoading(false);

      return data;
    } catch (ex) {
      console.error('Error al actualizar precio de copia:', ex);
      setLoading(false);
      return {
        success: false,
        error: 'Error al actualizar el precio'
      };
    }
  };

  const getPreciosAnillado = async () => {
    try {
      setLoading(true);

      const url = getUrl(`/getPreciosAnillado`);

      const { data } = await axios.get(url);
      setLoading(false);

      return data;
    } catch (ex) {
      console.error('Error al obtener precios de anillado:', ex);
      setLoading(false);
      return {
        success: false,
        error: 'Error al obtener los precios de anillado'
      };
    }
  };

  const updatePrecioAnillado = async (id: number, precio: number) => {
    try {
      setLoading(true);

      const url = getUrl(`/updatePrecioAnillado`);

      const { data } = await axios.put(url, { id, precio });
      setLoading(false);

      return data;
    } catch (ex) {
      console.error('Error al actualizar precio de anillado:', ex);
      setLoading(false);
      return {
        success: false,
        error: 'Error al actualizar el precio de anillado'
      };
    }
  };

  return {
    loading,
    getReport,
    validatePDF,
    calculatePrice,
    calculateShipping,
    createMPPreference,
    selectTable,
    selectTableByOrderId,
    selectTableByTrackingCode,
    updateTable,
    insertTable,
    deleteTable,
    bulkInsertTable,
    downloadOrderZip,
    getPreciosCopias,
    updatePrecioCopia,
    getPreciosAnillado,
    updatePrecioAnillado,
  };
};

export default useApi;
